<script setup>
import { ref, onMounted } from 'vue';
import { setMeta } from '@/utils/util';
import { ElMessage } from 'element-plus';
import imageCompression from 'browser-image-compression';
import JSZip from 'jszip';

const fileList = ref([]);
const loading = ref(false);
const quality = ref(0.8);
const maxWidthOrHeight = ref(1920);
const compressionResults = ref([]);
const compressedZipBlob = ref(null);

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

onMounted(() => {
  document.title = "图片压缩工具 - 在线图片压缩";
  setMeta("description", "免费在线图片压缩工具。在保持图片质量的同时减小文件大小。支持JPG、PNG等多种格式。");
  setMeta("keywords", "图片压缩,图片压缩工具,在线图片压缩,压缩图片大小,图片优化");
});

const handleUpload = async (files) => {
  if (!files.length) {
    ElMessage.warning('请先选择图片');
    return;
  }

  loading.value = true;
  const zip = new JSZip();
  const compressedFolder = zip.folder("compressed_images");
  compressionResults.value = [];
  compressedZipBlob.value = null;

  try {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: maxWidthOrHeight.value,
      useWebWorker: true,
      quality: quality.value,
    };

    // 压缩所有图片
    const compressPromises = files.map(async (file) => {
      try {
        const compressedBlob = await imageCompression(file.raw, options);
        const result = {
          name: file.name,
          originalSize: formatFileSize(file.raw.size),
          compressedSize: formatFileSize(compressedBlob.size),
          compressionRatio: ((1 - compressedBlob.size / file.raw.size) * 100).toFixed(1) + '%'
        };
        compressionResults.value.push(result);
        return {
          name: file.name,
          blob: compressedBlob
        };
      } catch (error) {
        console.error(`压缩 ${file.name} 时出错:`, error);
        return null;
      }
    });

    const compressedFiles = await Promise.all(compressPromises);

    // 添加到zip
    compressedFiles.forEach(file => {
      if (file) {
        compressedFolder.file(file.name, file.blob);
      }
    });

    // 生成zip blob
    compressedZipBlob.value = await zip.generateAsync({ type: "blob" });
    ElMessage.success('图片压缩完成！');
    fileList.value = [];
  } catch (error) {
    console.error('压缩失败:', error);
    ElMessage.error('图片压缩失败');
  } finally {
    loading.value = false;
  }
};

const downloadZip = () => {
  if (!compressedZipBlob.value) {
    ElMessage.warning('没有可下载的压缩图片');
    return;
  }
  const link = document.createElement('a');
  link.href = URL.createObjectURL(compressedZipBlob.value);
  link.download = "compressed_images.zip";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handleExceed = (files) => {
  ElMessage.warning(
    '一次最多可以选择10个文件'
  );
};

const beforeUpload = (file) => {
  const isImage = file.type.startsWith('image/');
  if (!isImage) {
    ElMessage.error('只能上传图片文件！');
  }
  return isImage;
};

const handleRemove = (file) => {
  const index = fileList.value.indexOf(file);
  if (index !== -1) {
    fileList.value.splice(index, 1);
  }
};
</script>

<template>
  <div class="tool-container">
    <h1 style="margin-bottom: 20px">图片压缩工具</h1>
    
    <div class="compression-options">
      <div class="option-group">
        <label>压缩质量 (0.1-1.0)：</label>
        <el-slider
          v-model="quality"
          :min="0.1"
          :max="1"
          :step="0.1"
          :marks="{
            0.1: '0.1',
            0.5: '0.5',
            1: '1.0'
          }"
        />
      </div>
      
      <div class="option-group">
        <label>最大宽度/高度（像素）：</label>
        <el-input-number
          v-model="maxWidthOrHeight"
          :min="100"
          :max="4000"
          :step="100"
        />
      </div>
    </div>

    <div class="upload-section">
      <el-upload
        class="upload-demo"
        drag
        multiple
        :auto-upload="false"
        :limit="10"
        :on-exceed="handleExceed"
        :before-upload="beforeUpload"
        :on-remove="handleRemove"
        v-model:file-list="fileList"
        accept="image/*"
      >
        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
        <div class="el-upload__text">
          将文件拖到此处，或<em>点击上传</em>
        </div>
        <template #tip>
          <div class="el-upload__tip">
            支持JPG、PNG等图片格式
          </div>
        </template>
      </el-upload>
      
      <div class="buttons">
        <el-button
          type="primary"
          @click="handleUpload(fileList)"
          :loading="loading"
          :disabled="fileList.length === 0"
        >
          开始压缩
        </el-button>
      </div>

      <!-- Add compression results table -->
      <div v-if="compressionResults.length > 0" class="compression-results">
        <div class="results-header">
          <h3>压缩结果</h3>
          <el-button
            type="primary"
            @click="downloadZip"
            :loading="loading"
            :disabled="!compressedZipBlob"
          >
            下载结果
          </el-button>
        </div>
        <el-table :data="compressionResults" stripe>
          <el-table-column prop="name" label="文件名" />
          <el-table-column prop="originalSize" label="原始大小" width="120" />
          <el-table-column prop="compressedSize" label="压缩后大小" width="120" />
          <el-table-column prop="compressionRatio" label="压缩率" width="100" />
        </el-table>
      </div>
    </div>

    <div class="introduce">
      <h2>什么是图片压缩工具？📸</h2>
      <p>
        图片压缩工具是一个免费的在线工具，可以帮助您减小图片文件的大小，同时保持良好的视觉质量。
        它非常适合优化网站图片、减少存储空间或通过电子邮件分享图片。
      </p>
      
      <h3>功能特点</h3>
      <ul>
        <li>支持批量压缩 - 一次可以压缩多张图片</li>
        <li>支持多种图片格式（JPG、PNG等）</li>
        <li>可调节的压缩质量</li>
        <li>控制最大图片尺寸</li>
        <li>自动打包下载压缩后的图片</li>
        <li>免费使用，无需注册</li>
      </ul>

      <h3>使用技巧</h3>
      <ul>
        <li>对于网页图片，质量设置为0.7-0.8通常能获得不错的效果</li>
        <li>最大宽度/高度设置可以确保图片不会过大</li>
        <li>一次最多可以压缩10张图片</li>
        <li>所有压缩后的图片会自动打包成ZIP文件下载</li>
        <li>支持拖拽多个文件快速上传</li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tool-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.compression-options {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  .option-group {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }

    label {
      display: block;
      margin-bottom: 10px;
      font-weight: bold;
      color: #606266;
    }
  }
}

.upload-section {
  margin-bottom: 30px;
  
  .buttons {
    margin-top: 20px;
    display: flex;
    gap: 10px;
    justify-content: center;
  }
}

.introduce {
  margin-top: 40px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  h2 {
    margin-bottom: 16px;
  }

  h3 {
    margin: 20px 0 10px;
    color: #2c3e50;
  }

  p {
    margin-bottom: 16px;
    line-height: 1.6;
    color: #606266;
  }

  ul {
    margin: 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 8px;
      color: #606266;
    }
  }
}

.compression-results {
  margin-top: 30px;
  
  .results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;

    h3 {
      margin: 0;
      color: #2c3e50;
    }
  }
}

:deep(.el-table) {
  margin-top: 10px;
  border-radius: 8px;
  overflow: hidden;
  
  .el-table__header-wrapper {
    background-color: #f8f9fa;
  }
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 200px;
}

:deep(.el-upload) {
  width: 100%;
}

:deep(.el-upload-list) {
  width: 100%;
}
</style> 